import {
  useEffect,
  useRef,
  useState
} from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  TabsGroup
} from '@unified-checkout/design-system';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  ALL_SERVICES_ID,
  BILLS_DISTRIBUTOR_HISTORY_TAB_ID,
  BILLS_HISTORY_TAB_ID,
  SAVED_BILLS_TAB_ID,
  SAVED_MERCHANTS_TAB_ID
} from '@app/dashboard/constants/Bills';
import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { getSavedMerchants } from '@app/dashboard/services/inner_layout/bills/get-saved-merchants';
import { getMainData } from '@app/dashboard/services/inner_layout/bills/get-main-data';
import { useLangStore } from '@app/dashboard/store/lang';

import { BillsTabContent } from './BillsTabContent';
import { Header } from './Header';

import { isDistributor } from './utils';
import styles from './bills.module.scss';


export const Bills = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const tabsRef = useRef(null);

  const [activeTab, setActiveTab] = useState(ALL_SERVICES_ID);

  const {
    isLoading: isSavedMerchantsPending,
    data: savedMerchantsData,
    refetch: refetchSavedMerchants,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SAVED_MERCHANTS, lang],
    queryFn: getSavedMerchants,
  });

  const {
    isPending: isBillsLoading,
    data: billsData,
    error: billsError
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MAIN_DATA, lang],
    queryFn: getMainData,
  })

  const savedMerchants = savedMerchantsData?.data?.data?.beneficiaries || [];

  useEffect(() => {
    if (!isBillsLoading) {
      amplitude.track(BILLS_EVENTS.VIEW_BILLS_PAGE, {
        [BILLS_EVENTS_PARAMS.IS_DISTRIBUTOR]: isDistributor(),
        [BILLS_EVENTS_PARAMS.BILLS_BALANCE]: billsData?.data?.data?.balance?.amount,
        [BILLS_EVENTS_PARAMS.STATUS]: billsData?.data?.status,
        [SHARED_EVENTS_PARAMS.ERR_DESCRIPTION]: (billsError as any)?.response?.data?.message,
      });
    }
  }, [isBillsLoading]);

  useEffect(() => {
    setActiveTab(ALL_SERVICES_ID);
    amplitude.track(BILLS_EVENTS.SELECT_ALL_SERVICES);
  }, []);

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === ALL_SERVICES_ID) {
      amplitude.track(BILLS_EVENTS.SELECT_ALL_SERVICES);
    }
    if (tabId === SAVED_BILLS_TAB_ID) {
      amplitude.track(BILLS_EVENTS.SELECTED_SAVED_BILLS);
    }
    if (tabId === BILLS_HISTORY_TAB_ID) {
      amplitude.track(BILLS_EVENTS.SELECTED_BILLS_HISTORY);
    }
  }

  const tabs = [
    { id: ALL_SERVICES_ID, label: t('innerLayout.billsPage.tabs.allServices') },
    { id: SAVED_BILLS_TAB_ID, label: t('innerLayout.billsPage.tabs.savedBills') },
    { id: BILLS_HISTORY_TAB_ID, label: t('innerLayout.billsPage.tabs.history') },
    ...(isDistributor() ? [
      { id: BILLS_DISTRIBUTOR_HISTORY_TAB_ID, label: t('innerLayout.billsPage.tabs.distributorHistory') },
      { id: SAVED_MERCHANTS_TAB_ID, label: t('innerLayout.billsPage.tabs.savedMerchant'), }] : [])
  ];

  return (
    <div className={styles["bills-wrapper"]}>
      <Header savedMerchants={savedMerchants} billsData={billsData} isBillsLoading={isBillsLoading} />

      <div className={styles['bills-wrapper__header__tabs']}>
        <div className={styles['bills-wrapper__header__tabs__group-wrapper']} ref={tabsRef}>
          <TabsGroup
            type="line"
            orientation="horizontal"
            tabsProps={tabs.map((tab) => ({
              active: activeTab === tab.id,
              disabled: false,
              tabLabel: tab.label,
              onClickHandler: () => handleTabClick(tab.id),
            }))}
          />
        </div>
      </div>

      <BillsTabContent
        activeTab={activeTab}
        savedMerchants={savedMerchants}
        isSavedMerchantsPending={isSavedMerchantsPending}
        refetchSavedMerchants={refetchSavedMerchants}
        billsData={billsData}
        isBillsLoading={isBillsLoading}
      />
    </div>
  )
}